<template>
  <Modal
    :headerTitle="'Team Uniform Settings'"
    @closeModal="removeTemporalProduct()"
    @confirm="saveNewProduct()"
  >
    <template #content>
      <div class="text-info">
        <p>
          You've added some team uniform items to your Snapsheet, wich we
          recommend to decorate with a Team Name and Number
        </p>
      </div>

      <article class="decorations-container">
        <div class="edit-decorations-container">
          <div class="colors-picker">
            <span>Colors</span>
            <ColorPicker
              :colorPickerMode="'multiple'"
              :colorsToChange="[projectColors[1], projectColors[2]]"
              @selectColor="handleSelectColor"
            />
          </div>
          <AddTextStampInput
            :label="'Team Name'"
            :textValue="uniformMascot"
            @changeTextValue="handleChangeTextValue"
          />
        </div>
        <ProductDisplay
          :product="temporalProduct"
          :threekitLayers="threekitLayers"
        />
      </article>

      <transition name="fade">
        <div class="tk-modal-loader" v-if="loading">
          <Loader :withMessage="true" :message="'Applying changes...'" />
        </div>
      </transition>
    </template>
  </Modal>
</template>

<script>
import AddTextStampInput from "@/components/Projects/Project/ActiveProduct/Decorations/AddTextStampInput.vue";
import ColorPicker from "@/components/Projects/Project/Colors/ColorPicker.vue";
import Loader from "@/components/Utils/Loader.vue";
import Modal from "@/components/Utils/Modal";
import ProductDisplay from "@/components/Projects/Product/ProductDisplay/ProductDisplay";
import { mapGetters, mapActions, mapState } from "vuex";
import { debounce } from "debounce";

export default {
  name: "SnapsheetEditNewUniformProductModal",
  components: { AddTextStampInput, ColorPicker, Loader, Modal, ProductDisplay },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapState("project", ["temporalProduct", "uniformMascot"]),

    ...mapGetters("project", [
      "projectColors",
      "uniformFillColor",
      "uniformOutlineColor",
      "uniformMascot",
    ]),

    ...mapGetters("threekit", ["get2DGroupedLayers"]),
    ...mapState("threekit", ["sublimationColors"]),

    threekitDesignItem() {
      return this.temporalProduct.threekit_designs?.length
        ? this.temporalProduct.threekit_designs[0]
        : this.temporalProduct?.design?.[0]
        ? this.temporalProduct?.design?.[0]
        : null;
    },

    threekitLayers() {
      if (
        this.threekitDesignItem &&
        this.sublimationColors?.length > 0 &&
        this.temporalProduct.isSublimable
      ) {
        const colors = {
          color1: this.projectColors[0]?.color?.chromagear_name,
          color2: this.projectColors[1]?.color?.chromagear_name,
          color3: this.projectColors[2]?.color?.chromagear_name,
        };

        return this.get2DGroupedLayers({
          styleName: this.temporalProduct.style_id,
          designName: this.threekitDesignItem?.design_name,
          ...colors,
        });
      } else {
        return this.get2DGroupedLayers({
          styleName: this.temporalProduct.style_id,
          color1: this.projectColors[0]?.color?.stock_name,
        });
      }
    },
  },
  methods: {
    ...mapActions("project", [
      "applyProductDecorationsChanges",
      "removeTemporalProduct",
      "saveNewProduct",
      "updateDecorationsForProjectProductsLayouts",
    ]),
    ...mapActions("snapsheet", [
      "updateSnapsheetSettings",
      "updateSnapsheetColor",
    ]),

    handleChangeTextValue: debounce(async function (value) {
      console.log(value);
      try {
        this.loading = true;
        this.$store.commit("project/SET_UNIFORM_MASCOT", value);

        setTimeout(() => {
          this.$nextTick(async () => {
            try {
              //Save changes in the active decoration
              await this.updateSnapsheetSettings({
                dbKey: "team_name",
                field: "teamName",
                value,
              });
              await this.updateDecorationsForProjectProductsLayouts();
            } catch (error) {
              console.error(error);
            } finally {
              this.loading = false;
            }
          });
        }, 100);
      } catch (error) {
        console.log(error);
      }
    }, 500),

    handleSelectColor({ color, colorKey }) {
      try {
        if (colorKey === "color_2" || colorKey === "color_3") {
          this.loading = true;
          //Update decorations colors
          if (colorKey === "color_2") {
            this.$store.commit("project/SET_SPIRITWEAR_FILL_COLOR", color.id);
            this.$store.commit("project/SET_UNIFORM_FILL_COLOR", color.id);
          } else {
            this.$store.commit(
              "project/SET_SPIRITWEAR_OUTLINE_COLOR",
              color.id
            );
            this.$store.commit("project/SET_UNIFORM_OUTLINE_COLOR", color.id);
          }

          //Set Second/Third Snapsheet Color
          this.$store.commit("snapsheet/SET_COLOR", {
            colorId: color.id,
            colorKey,
          });
        }

        setTimeout(async () => {
          console.log("update");
          await this.updateDecorationsForProjectProductsLayouts();
          await this.updateSnapsheetColor({ colorKey, colorId: color.id });
          this.loading = false;
        }, 500);
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .modal-container {
  width: 50% !important;
  // height: 60% !important;
  max-width: 720px !important;
  max-height: 720px !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  border-radius: 3px;

  .header,
  .content,
  .save-cancel-container {
    padding: 0 2rem;
    width: 100%;
  }

  .header {
    border-bottom: 1px solid $borderColor;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
    padding: 1.2rem 2rem;
    width: 100%;
    p {
      text-transform: capitalize;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    margin-bottom: 0.5rem;
    height: 60%;
    flex-grow: 1;

    .text-info {
      background: rgba(238, 243, 255, 1);
      border-radius: 1rem;
      border: 1px solid $blue;
      margin-bottom: 2rem;
      padding: 1rem;
      width: 100%;
      p {
        color: $blue;
        width: 75%;
        margin: auto;
        text-align: center;
      }
    }

    .decorations-container {
      display: flex;
      justify-content: space-between;
      flex-grow: 1;
      width: 100%;
      height: 50%;
      .edit-decorations-container {
        width: 45%;

        label {
          width: 100%;
        }

        .colors-picker {
          display: flex;
          flex-direction: column;
          span {
            margin-bottom: 0.5rem;
          }
        }
      }
      .product-display {
        width: 50% !important;
        border: 1px solid $borderColor;
        border-radius: 1rem;
      }
    }
  }
  .buttons-container {
    margin: auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 1rem;
    padding-bottom: 1rem;
    border-top: 1px solid $borderColor;
    .btn {
      background: black;
      color: white;
      margin-left: auto;
      width: 10rem;
      text-transform: capitalize;
    }
  }
}
</style>
